import React, { useMemo, useState } from 'react'
import { OWAuthDataModel } from '~/models/OWAuthDataModel'
import * as GroupUtils from '~/utils/GroupUtils'
import { hasFeature } from '~/utils/OWUtils'
import { Features } from '~/utils/Constants'
import { Company, Employee } from '~/app/types'
import { Global } from '~/global'

const {
    IS_AUTHENTICATED,
    PROFILE_CELEBRATIONS_ENABLED,
    HIDE_NAVBAR_SEARCH,
    HIDE_TOOLS,
    TAGS_ENABLED,
    IS_GUEST,
    IS_GHOST,
    ONLY_LOGOUT,
} = Global

type OWContextType = {
    employee?: Employee | null
    company?: Company | null
    timelineModerationDatetime?: string
    setTimelineModerationDatetime: (date: string | undefined) => void
    isAuthenticated: boolean
    isGuest: boolean
    isGhost: boolean
    isAdminActive: boolean
    hideSearch: boolean
    hideNav: boolean
    tagsEnabled: boolean
    groupsEnabled: boolean
    dlistsEnabled: boolean
    onlyLogout: boolean
    communitiesEnabled: boolean
    configurationModeEnabled: boolean
    dlistsCardEnabled: boolean
    hideProfileStrength: boolean
    teamsDemo: boolean
    transferPortalEnabled: boolean
    profileCelebrationsEnabled: boolean
    workAnywhereEnabled: boolean
}
export const OWContext = React.createContext<OWContextType>({
    setTimelineModerationDatetime: () => {
        return
    },
    isAdminActive: false,
    isAuthenticated: false,
    isGuest: false,
    isGhost: false,
    hideSearch: false,
    hideNav: false,
    tagsEnabled: false,
    groupsEnabled: false,
    dlistsEnabled: false,
    onlyLogout: false,
    communitiesEnabled: false,
    configurationModeEnabled: false,
    dlistsCardEnabled: false,
    hideProfileStrength: false,
    teamsDemo: false,
    transferPortalEnabled: false,
    profileCelebrationsEnabled: false,
    workAnywhereEnabled: false,
})
export const OWContextProvider = ({ children }) => {
    const [timelineModerationDatetime, setTimelineModerationDatetime] = useState<string | undefined>(
        OWAuthDataModel.getAuthCompany()?.timeline_moderation_datetime ?? undefined
    )
    const context = useMemo(() => {
        return {
            employee: OWAuthDataModel.getAuthEmployee(),
            company: OWAuthDataModel.getAuthCompany(),
            timelineModerationDatetime,
            setTimelineModerationDatetime,
            isAuthenticated: IS_AUTHENTICATED,
            isGuest: IS_GUEST,
            isGhost: IS_GHOST,
            isAdminActive: OWAuthDataModel.isAdminActive(),
            hideSearch: HIDE_NAVBAR_SEARCH,
            hideNav: HIDE_TOOLS,
            tagsEnabled: TAGS_ENABLED,
            groupsEnabled: GroupUtils.groupsEnabled(),
            dlistsEnabled: GroupUtils.dlistsEnabled(),
            onlyLogout: ONLY_LOGOUT,
            configurationModeEnabled: hasFeature(Features.PRELAUNCH_MODE),
            dlistsCardEnabled: hasFeature(Features.SHOW_DLISTS_PROFILE_CARD),
            hideProfileStrength: hasFeature(Features.HIDE_PROFILE_STRENGTH),
            teamsDemo: hasFeature(Features.SHOW_TEAMS_DEMO),
            communitiesEnabled: hasFeature(Features.COMMUNITIES_ENABLED),
            transferPortalEnabled: hasFeature(Features.TRANSFER_PORTAL_ENABLED),
            profileCelebrationsEnabled: PROFILE_CELEBRATIONS_ENABLED,
            workAnywhereEnabled: hasFeature(Features.WORK_ANYWHERE_ENABLED),
        }
    }, [timelineModerationDatetime])

    return <OWContext.Provider value={context}>{children}</OWContext.Provider>
}

export const useOWContext = () => React.useContext<OWContextType>(OWContext)
